import React, { useEffect, useState } from "react";

import {
    Modal,
    Form,
    Select,
    Button,
    Typography,
    Divider,
    Space,
    Spin,
    Input,
    Row,
    Col,
    Tag,
    Alert,
} from "antd";

import {
    ArrowUpOutlined,
    CalendarOutlined,
    BookOutlined,
    CheckCircleOutlined,
    UserOutlined,
    TeamOutlined,
    SafetyCertificateOutlined,
    InfoCircleOutlined,
    GraduationCap,
    CloseOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";
import Swal from "sweetalert2";

import api from "../../api/api";


const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;


const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C07500",
    background: "#F3F6FB",
    white: "#FFFFFF",
    text: "#243447",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#15803D",
    danger: "#DC2626",
};


const PromotionStudentRecord = ({
    open,
    onClose,
    student,
    refresh,
}) => {
    const [form] = Form.useForm();

    const [academicYears, setAcademicYears] = useState([]);
    const [gradeClasses, setGradeClasses] = useState([]);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);


    // =========================================
    // LOAD PROMOTION OPTIONS
    // Existing API endpoints preserved
    // =========================================

    const fetchPromotionData = async () => {
        try {
            setLoading(true);

            const [years, grades] = await Promise.all([
                api.get("/academic-years/"),
                api.get("/api/users/grade-classes/"),
            ]);

            setAcademicYears(
                Array.isArray(years.data)
                    ? years.data
                    : years.data?.results || []
            );

            setGradeClasses(
                Array.isArray(grades.data)
                    ? grades.data
                    : grades.data?.results || []
            );
        } catch (error) {
            console.error(
                "Loading promotion options error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Unable to Load Options",
                text:
                    error.response?.data?.detail ||
                    "Unable to load academic years and grade classes. Please try again.",
                confirmButtonColor: COLORS.navy,
                customClass: {
                    popup: "promotion-swal-popup",
                },
            });
        } finally {
            setLoading(false);
        }
    };


    // =========================================
    // RESET AND INITIALIZE FORM
    // =========================================

    useEffect(() => {
        if (!open) return;

        form.resetFields();
        fetchPromotionData();

    }, [open, student]);


    // =========================================
    // CLOSE MODAL
    // =========================================

    const handleClose = () => {
        if (submitting) return;

        form.resetFields();
        onClose();
    };


    // =========================================
    // PROMOTION SUBMISSION
    // Existing endpoint and payload preserved
    // =========================================

    const handleSubmit = async (values) => {
        if (!student?.user_id) {
            Swal.fire({
                icon: "warning",
                title: "Student Information Missing",
                text: "Student information is unavailable. Please select a student again.",
                confirmButtonColor: COLORS.navy,
            });

            return;
        }

        const selectedYear = academicYears.find(
            (year) =>
                year.id === values.next_academic_year
        );

        const selectedGrade = gradeClasses.find(
            (grade) =>
                grade.id === values.next_grade_class
        );

        const confirm = await Swal.fire({
            title: "Confirm Student Promotion",

            html: `
                <div style="text-align:left;line-height:1.8">
                    <p style="margin-bottom:12px;color:#64748b">
                        You are about to promote the following student.
                    </p>

                    <div style="
                        background:#f3f6fb;
                        border:1px solid #e2e8f0;
                        border-radius:12px;
                        padding:15px;
                        margin-bottom:12px
                    ">
                        <div style="
                            font-size:11px;
                            color:#718096;
                            text-transform:uppercase;
                            font-weight:700
                        ">
                            Student
                        </div>

                        <div style="
                            color:#062a4b;
                            font-weight:800;
                            font-size:15px
                        ">
                            ${String(student.full_name || "Student")
                                .replace(/&/g, "&amp;")
                                .replace(/</g, "&lt;")
                                .replace(/>/g, "&gt;")
                                .replace(/"/g, "&quot;")
                                .replace(/'/g, "&#039;")}
                        </div>
                    </div>

                    <div style="
                        background:#fff9e8;
                        border:1px solid #f6c944;
                        border-radius:12px;
                        padding:15px
                    ">
                        <div style="
                            font-size:11px;
                            color:#718096;
                            text-transform:uppercase;
                            font-weight:700
                        ">
                            Destination Academic Year
                        </div>

                        <div style="
                            color:#062a4b;
                            font-weight:800
                        ">
                            ${String(selectedYear?.name || "Selected year")
                                .replace(/&/g, "&amp;")
                                .replace(/</g, "&lt;")
                                .replace(/>/g, "&gt;")
                                .replace(/"/g, "&quot;")
                                .replace(/'/g, "&#039;")}
                        </div>

                        <div style="
                            font-size:11px;
                            color:#718096;
                            text-transform:uppercase;
                            font-weight:700;
                            margin-top:10px
                        ">
                            Destination Grade
                        </div>

                        <div style="
                            color:#062a4b;
                            font-weight:800
                        ">
                            ${String(selectedGrade?.name || "Selected grade")
                                .replace(/&/g, "&amp;")
                                .replace(/</g, "&lt;")
                                .replace(/>/g, "&gt;")
                                .replace(/"/g, "&quot;")
                                .replace(/'/g, "&#039;")}
                        </div>
                    </div>

                    <p style="
                        margin-top:14px;
                        font-size:12px;
                        color:#718096
                    ">
                        Please verify the destination before continuing.
                    </p>
                </div>
            `,

            icon: "question",

            showCancelButton: true,

            confirmButtonText: "Yes, Promote Student",
            cancelButtonText: "Review Details",

            confirmButtonColor: COLORS.navy,
            cancelButtonColor: "#64748B",

            reverseButtons: true,
            focusCancel: true,

            customClass: {
                popup: "promotion-swal-popup",
            },
        });

        if (!confirm.isConfirmed) return;

        try {
            setSubmitting(true);

            const payload = {
                student_id: student.user_id,
                next_academic_year: values.next_academic_year,
                next_grade_class: values.next_grade_class,
                remarks: values.remarks || "",
            };

            const response = await api.post(
                "/student-promote/",
                payload
            );

            await Swal.fire({
                icon: "success",
                title: "Promotion Successful",
                text:
                    response.data.message ||
                    "The student has been promoted successfully.",

                confirmButtonText: "Continue",
                confirmButtonColor: COLORS.navy,

                customClass: {
                    popup: "promotion-swal-popup",
                },
            });

            form.resetFields();

            onClose();

            if (refresh) {
                refresh(student.user_id);
            }

        } catch (error) {
            console.error(
                "Student promotion error:",
                error
            );

            const errorData = error.response?.data;

            const errorMessage =
                errorData?.message ||
                errorData?.detail ||
                (
                    typeof errorData === "string"
                        ? errorData
                        : null
                ) ||
                "Unable to promote this student. Please verify the details and try again.";

            Swal.fire({
                icon: "error",
                title: "Promotion Failed",
                text: errorMessage,
                confirmButtonColor: COLORS.navy,

                customClass: {
                    popup: "promotion-swal-popup",
                },
            });

        } finally {
            setSubmitting(false);
        }
    };


    // =========================================
    // SHARED FIELD STYLING
    // =========================================

    const selectStyle = {
        width: "100%",
    };


    const selectDropdownStyle = {
        maxHeight: 220,
        overflowY: "auto",
    };


    // =========================================
    // MODAL UI
    // =========================================

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            centered
            width={680}
            destroyOnClose
            maskClosable={false}
            keyboard={!submitting}
            closable={false}
            styles={{
                body: {
                    padding: 0,
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                    borderRadius: 20,
                    boxShadow: "0 25px 80px rgba(6,42,75,0.22)",
                },
                mask: {
                    backdropFilter: "blur(4px)",
                    backgroundColor: "rgba(6,22,40,0.55)",
                },
            }}
        >
            <motion.div
                initial={{
                    opacity: 0,
                    y: 20,
                    scale: 0.98,
                }}
                animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                }}
                transition={{
                    duration: 0.25,
                }}
                style={{
                    width: "100%",
                    background: COLORS.white,
                }}
            >
                {/* =================================
                    PREMIUM MODAL HEADER
                ================================= */}

                <div
                    style={{
                        position: "relative",
                        overflow: "hidden",

                        background: `
                            linear-gradient(
                                120deg,
                                ${COLORS.navy} 0%,
                                ${COLORS.deepBlue} 100%
                            )
                        `,

                        padding: "26px 28px 23px",
                        color: COLORS.white,
                    }}
                >
                    {/* Decorative accent */}

                    <div
                        style={{
                            position: "absolute",
                            right: -45,
                            top: -75,
                            width: 180,
                            height: 180,
                            borderRadius: "50%",
                            border: "25px solid rgba(246,201,68,0.10)",
                            pointerEvents: "none",
                        }}
                    />

                    <div
                        style={{
                            position: "absolute",
                            right: 90,
                            bottom: -95,
                            width: 140,
                            height: 140,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.035)",
                            pointerEvents: "none",
                        }}
                    />

                    <div
                        style={{
                            position: "relative",
                            zIndex: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 16,
                        }}
                    >
                        <Space
                            align="start"
                            size={15}
                        >
                            <div
                                style={{
                                    width: 54,
                                    height: 54,
                                    flexShrink: 0,

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    borderRadius: 15,
                                    background: COLORS.gold,
                                    color: COLORS.navy,

                                    boxShadow:
                                        "0 6px 18px rgba(0,0,0,0.16)",
                                }}
                            >
                                <ArrowUpOutlined
                                    style={{
                                        fontSize: 27,
                                    }}
                                />
                            </div>

                            <div>
                                <Title
                                    level={3}
                                    style={{
                                        color: COLORS.white,
                                        margin: "1px 0 7px",
                                        fontSize: 23,
                                        fontWeight: 850,
                                        letterSpacing: "-0.4px",
                                    }}
                                >
                                    Promote Student
                                </Title>

                                <Text
                                    style={{
                                        display: "block",
                                        color: "rgba(255,255,255,0.76)",
                                        fontSize: 12,
                                        lineHeight: 1.7,
                                        maxWidth: 390,
                                    }}
                                >
                                    Assign the student to a new academic
                                    year and grade class.
                                </Text>
                            </div>
                        </Space>

                        <Button
                            type="text"
                            aria-label="Close promotion dialog"
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                            disabled={submitting}
                            style={{
                                color: COLORS.white,
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                                background: "rgba(255,255,255,0.10)",
                                flexShrink: 0,
                            }}
                        />
                    </div>

                    <Divider
                        style={{
                            borderColor: "rgba(255,255,255,0.14)",
                            margin: "21px 0 15px",
                        }}
                    />

                    <Space
                        wrap
                        size={[8, 8]}
                    >
                        <Tag
                            icon={<SafetyCertificateOutlined />}
                            style={{
                                margin: 0,
                                padding: "4px 10px",
                                borderRadius: 7,
                                background: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                border: "1px solid rgba(255,255,255,0.12)",
                                fontSize: 11,
                                fontWeight: 700,
                            }}
                        >
                            Academic Management
                        </Tag>

                        <Tag
                            icon={<CheckCircleOutlined />}
                            style={{
                                margin: 0,
                                padding: "4px 10px",
                                borderRadius: 7,
                                background: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                border: "1px solid rgba(255,255,255,0.12)",
                                fontSize: 11,
                                fontWeight: 700,
                            }}
                        >
                            Verified Submission
                        </Tag>
                    </Space>
                </div>


                {/* =================================
                    MODAL BODY
                ================================= */}

                <div
                    style={{
                        padding: "25px 28px 28px",
                        background: COLORS.background,
                    }}
                >
                    {loading ? (
                        <div
                            style={{
                                minHeight: 300,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                gap: 16,
                            }}
                        >
                            <Spin
                                size="large"
                                description="Loading promotion options..."
                            />

                            <Text
                                style={{
                                    color: COLORS.muted,
                                    fontSize: 12,
                                }}
                            >
                                Preparing academic years and grade classes...
                            </Text>
                        </div>
                    ) : (
                        <>
                            {/* STUDENT SUMMARY */}

                            <div
                                style={{
                                    marginBottom: 23,
                                    padding: 18,

                                    borderRadius: 14,
                                    background: COLORS.white,
                                    border: `1px solid ${COLORS.border}`,

                                    boxShadow:
                                        "0 3px 12px rgba(6,42,75,0.025)",
                                }}
                            >
                                <Space
                                    align="center"
                                    size={12}
                                    style={{
                                        marginBottom: 16,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 11,
                                            background: "#EAF1F9",
                                            color: COLORS.deepBlue,

                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <UserOutlined
                                            style={{
                                                fontSize: 20,
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <Text
                                            style={{
                                                display: "block",
                                                color: COLORS.navy,
                                                fontWeight: 850,
                                                fontSize: 14,
                                            }}
                                        >
                                            Student Information
                                        </Text>

                                        <Text
                                            style={{
                                                display: "block",
                                                color: COLORS.muted,
                                                fontSize: 11,
                                                marginTop: 3,
                                            }}
                                        >
                                            Confirm the student before proceeding.
                                        </Text>
                                    </div>
                                </Space>

                                <Row
                                    gutter={[16, 16]}
                                >
                                    <Col
                                        xs={24}
                                        sm={12}
                                    >
                                        <div
                                            style={{
                                                padding: 13,
                                                borderRadius: 10,
                                                background: "#F8FAFC",
                                                border: `1px solid ${COLORS.border}`,
                                                height: "100%",
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    display: "block",
                                                    fontSize: 10,
                                                    color: COLORS.muted,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.6,
                                                    fontWeight: 750,
                                                    marginBottom: 7,
                                                }}
                                            >
                                                Full Name
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    color: COLORS.navy,
                                                    fontSize: 13,
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                {student?.full_name ||
                                                    "Student unavailable"}
                                            </Text>
                                        </div>
                                    </Col>

                                    <Col
                                        xs={24}
                                        sm={12}
                                    >
                                        <div
                                            style={{
                                                padding: 13,
                                                borderRadius: 10,
                                                background: "#F8FAFC",
                                                border: `1px solid ${COLORS.border}`,
                                                height: "100%",
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    display: "block",
                                                    fontSize: 10,
                                                    color: COLORS.muted,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.6,
                                                    fontWeight: 750,
                                                    marginBottom: 7,
                                                }}
                                            >
                                                Current Grade
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    color: COLORS.deepBlue,
                                                    fontSize: 13,
                                                }}
                                            >
                                                {student?.grade_class_name ||
                                                    "Not assigned"}
                                            </Text>
                                        </div>
                                    </Col>

                                    <Col span={24}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                padding: "11px 13px",
                                                borderRadius: 10,
                                                background: "#FFF9E8",
                                                border: "1px solid #FBE7A1",
                                            }}
                                        >
                                            <TeamOutlined
                                                style={{
                                                    color: COLORS.orange,
                                                    fontSize: 17,
                                                }}
                                            />

                                            <div
                                                style={{
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        display: "block",
                                                        fontSize: 10,
                                                        color: COLORS.muted,
                                                        fontWeight: 750,
                                                        textTransform: "uppercase",
                                                        letterSpacing: 0.5,
                                                    }}
                                                >
                                                    Current Division
                                                </Text>

                                                <Text
                                                    strong
                                                    style={{
                                                        display: "block",
                                                        color: COLORS.navy,
                                                        fontSize: 12,
                                                        marginTop: 3,
                                                        overflowWrap: "anywhere",
                                                    }}
                                                >
                                                    {student?.division_assigned_name ||
                                                        "Not assigned"}
                                                </Text>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>


                            {/* PROMOTION FORM */}

                            <div
                                style={{
                                    padding: 20,
                                    borderRadius: 14,
                                    background: COLORS.white,
                                    border: `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Space
                                    align="center"
                                    size={10}
                                    style={{
                                        marginBottom: 5,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 10,
                                            background: "#FFF7DB",
                                            color: COLORS.orange,

                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <BookOutlined
                                            style={{
                                                fontSize: 19,
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <Title
                                            level={5}
                                            style={{
                                                margin: 0,
                                                color: COLORS.navy,
                                                fontSize: 15,
                                                fontWeight: 850,
                                            }}
                                        >
                                            Promotion Destination
                                        </Title>

                                        <Text
                                            style={{
                                                color: COLORS.muted,
                                                fontSize: 11,
                                            }}
                                        >
                                            Select the student's destination.
                                        </Text>
                                    </div>
                                </Space>

                                <Divider
                                    style={{
                                        borderColor: COLORS.border,
                                        margin: "16px 0 20px",
                                    }}
                                />

                                <Form
                                    layout="vertical"
                                    form={form}
                                    onFinish={handleSubmit}
                                    requiredMark="optional"
                                >
                                    {/* NEXT ACADEMIC YEAR */}

                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: COLORS.text,
                                                    fontWeight: 750,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Next Academic Year
                                            </span>
                                        }
                                        name="next_academic_year"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Select the next academic year.",
                                            },
                                        ]}
                                    >
                                        <Select
                                            size="large"
                                            placeholder="Search and select academic year"
                                            showSearch
                                            allowClear
                                            optionFilterProp="children"
                                            suffixIcon={
                                                <CalendarOutlined
                                                    style={{
                                                        color: COLORS.deepBlue,
                                                    }}
                                                />
                                            }
                                            style={selectStyle}
                                            dropdownStyle={selectDropdownStyle}
                                            getPopupContainer={(trigger) =>
                                                trigger.parentElement
                                            }
                                        >
                                            {academicYears.map((year) => (
                                                <Option
                                                    key={year.id}
                                                    value={year.id}
                                                >
                                                    {year.name ||
                                                        year.academic_year ||
                                                        year.year ||
                                                        `Academic Year ${year.id}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>


                                    {/* NEXT GRADE */}

                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: COLORS.text,
                                                    fontWeight: 750,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Next Grade Class
                                            </span>
                                        }
                                        name="next_grade_class"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Select the next grade class.",
                                            },
                                        ]}
                                    >
                                        <Select
                                            size="large"
                                            placeholder="Search and select destination grade"
                                            showSearch
                                            allowClear
                                            optionFilterProp="children"
                                            suffixIcon={
                                                <BookOutlined
                                                    style={{
                                                        color: COLORS.deepBlue,
                                                    }}
                                                />
                                            }
                                            style={selectStyle}
                                            dropdownStyle={selectDropdownStyle}
                                            getPopupContainer={(trigger) =>
                                                trigger.parentElement
                                            }
                                        >
                                            {gradeClasses.map((grade) => (
                                                <Option
                                                    key={grade.id}
                                                    value={grade.id}
                                                >
                                                    {grade.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>


                                    {/* OPTIONAL REMARKS */}

                                    <Form.Item
                                        label={
                                            <span
                                                style={{
                                                    color: COLORS.text,
                                                    fontWeight: 750,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Promotion Remarks{" "}
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    (Optional)
                                                </Text>
                                            </span>
                                        }
                                        name="remarks"
                                    >
                                        <TextArea
                                            rows={3}
                                            maxLength={1000}
                                            showCount
                                            placeholder="Enter any relevant remarks about this student's promotion..."
                                            style={{
                                                resize: "vertical",
                                                borderRadius: 10,
                                                fontSize: 12,
                                                padding: 12,
                                            }}
                                        />
                                    </Form.Item>


                                    {/* INFORMATION */}

                                    <Alert
                                        type="info"
                                        showIcon
                                        icon={<InfoCircleOutlined />}
                                        message="Review before promoting"
                                        description="Verify the selected academic year and grade class carefully before submitting the promotion."
                                        style={{
                                            borderRadius: 10,
                                            marginBottom: 20,
                                            fontSize: 12,
                                        }}
                                    />


                                    {/* ACTION BUTTONS */}

                                    <Row
                                        gutter={[10, 10]}
                                    >
                                        <Col
                                            xs={24}
                                            sm={9}
                                        >
                                            <Button
                                                block
                                                size="large"
                                                icon={<CloseOutlined />}
                                                onClick={handleClose}
                                                disabled={submitting}
                                                style={{
                                                    minHeight: 46,
                                                    borderRadius: 10,
                                                    fontWeight: 750,
                                                    fontSize: 12,
                                                    color: COLORS.text,
                                                    borderColor: COLORS.border,
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </Col>

                                        <Col
                                            xs={24}
                                            sm={15}
                                        >
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                block
                                                size="large"
                                                loading={submitting}
                                                icon={
                                                    !submitting ? (
                                                        <CheckCircleOutlined />
                                                    ) : undefined
                                                }
                                                style={{
                                                    minHeight: 46,
                                                    borderRadius: 10,

                                                    background: COLORS.navy,
                                                    borderColor: COLORS.navy,

                                                    fontWeight: 850,
                                                    fontSize: 12,

                                                    boxShadow:
                                                        "0 5px 14px rgba(6,42,75,0.13)",
                                                }}
                                            >
                                                {submitting
                                                    ? "Processing Promotion..."
                                                    : "Confirm Student Promotion"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>


                            {/* FOOTER */}

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 7,
                                    marginTop: 18,
                                    textAlign: "center",
                                }}
                            >
                                <SafetyCertificateOutlined
                                    style={{
                                        color: COLORS.success,
                                        fontSize: 14,
                                    }}
                                />

                                <Text
                                    style={{
                                        color: COLORS.muted,
                                        fontSize: 10,
                                    }}
                                >
                                    Academic promotion management
                                </Text>
                            </div>
                        </>
                    )}
                </div>
            </motion.div>
        </Modal>
    );
};

export default PromotionStudentRecord;
